import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  defaultPurchasingPriority,
  determineEffectivePurchasingPriority,
  EffectivePriority,
  getChildPurchasingPriorityOverrides,
} from "@/features/purchasing-priority/purchasing-priority-utils";
import { supabase } from "@/lib/supabase";
import { TablesInsert } from "@/types/supabase-types";
import { getJobTrackingDataQueryKey } from "../../components/withJobTrackingData";
import { Job } from "../job-by-name";
import { JobTrackingData, updateJobTrackingData } from "../job-tracking-data";
import { BomNode } from "./bom-hierarchy-generator";
import {
  BomPathPurchasingPriority,
  processPurchasingPriorityRecord,
  PurchasingPriorities,
} from "./purchasing-priorities";

export type UpdatePurchasingPriorityMutation = ReturnType<typeof useUpdatePurchasingPriorityMutation>;

export type PriorityMutationType = PriorityMutationVariables["action"];

/**
 * The variables that can be passed to this mutation.
 *
 * This mutation combines two different actions into the same hook rather than creating two separate hooks. This is done
 * so that consumers can use the same mutation for various components and only enable one at a time.
 */
export type PriorityMutationVariables =
  | { action: "upsert"; upsertDetails: PriorityUpsertDetails[] }
  | { action: "delete"; recordsToDelete: BomPathPurchasingPriority[] };

export interface PriorityUpsertDetails {
  path: string;
  jobSpecific: boolean;
  priority: number;
  recordToUpdate: BomPathPurchasingPriority | null | undefined;
}

/**
 * Mutation hook that can be used to upsert purchasing priorities for a specific node in the BOM hierarchy, or delete
 * a series of purchasing priority database records.
 */
export function useUpdatePurchasingPriorityMutation(job: Job) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: PriorityMutationVariables) => {
      const action = variables.action;
      if (action === "upsert") {
        // Create a set of database records to upsert based of the provided upsert details
        const priorityRecords: TablesInsert<"BomPathPurchasingPriority">[] = variables.upsertDetails.map(
          ({ path, jobSpecific, priority, recordToUpdate }) => ({
            ...(recordToUpdate && { id: recordToUpdate.id, version: recordToUpdate.version }),
            jobId: jobSpecific ? job.id : null,
            path,
            priority,
          }),
        );

        // Upsert the priority records in the database
        const alteredRecords = (
          await supabase
            .from("BomPathPurchasingPriority")
            .upsert(priorityRecords, { onConflict: "jobId,path" })
            .select()
            .throwOnError()
        ).data.map(processPurchasingPriorityRecord);
        return { action, alteredRecords };
      } else {
        // Delete the purchasing priority record for the specified path
        const idsToDelete = variables.recordsToDelete.map((x) => x.id);
        await supabase.from("BomPathPurchasingPriority").delete().in("id", idsToDelete).throwOnError();
        return { action, alteredRecords: variables.recordsToDelete };
      }
    },
    onSuccess: ({ action, alteredRecords }) => {
      // Update the job tracking data in the query cache
      queryClient.setQueryData<JobTrackingData>(getJobTrackingDataQueryKey(job.number), (old) => {
        // If the job tracking data is not loaded, return early
        if (!old) return old;

        // Update the purchasing priorities
        const purchasingPriorities = applyMutationsToPurchasingPriorities(
          old.purchasingPriorities,
          action,
          alteredRecords,
        );

        // Recompute the purchasing priority details for each node in the bom hierarchy by applying each altered priority
        // record one-by-one. In theory we could do this in a single iteration, but this approach is simpler and should
        // provide good enough performance for the use case
        // NOTE: We could just get the dependency graph to re-compute this, which is very quick, but by doing a targeted
        // update we can reduce the number of objects updates and re-renders in the UI
        const salesOrderBomHierarchy = applyMutationsToBomHierarchy(action, old.salesOrderBomHierarchy, alteredRecords);

        // Asynchronously recompute the job tracking data graph, then update the query cache with the new data
        void updateJobTrackingData(old, [], { purchasingPriorities, salesOrderBomHierarchy }).then((updatedData) => {
          queryClient.setQueryData<JobTrackingData>(getJobTrackingDataQueryKey(job.number), updatedData);
        });

        // Return undefined to skip the updating the query synchronously (will be updated async)
        return undefined;
      });
    },
  });
}

function applyMutationsToPurchasingPriorities(
  old: PurchasingPriorities,
  action: PriorityMutationType,
  alteredRecords: BomPathPurchasingPriority[],
): PurchasingPriorities {
  const alteredRecordsById = new Map(alteredRecords.map((x) => [x.id, x]));
  return {
    salesOrderPriorities: applyMutationsToPurchasingPrioritiesInternal(
      old.salesOrderPriorities,
      action,
      alteredRecordsById,
    ),
    templatePriorities: applyMutationsToPurchasingPrioritiesInternal(
      old.templatePriorities,
      action,
      alteredRecordsById,
    ),
  };
}

function applyMutationsToPurchasingPrioritiesInternal(
  old: BomPathPurchasingPriority[],
  action: PriorityMutationType,
  alteredRecordsById: Map<number, BomPathPurchasingPriority>,
): BomPathPurchasingPriority[] {
  return old
    .map((oldRecord) => {
      // Get the matching altered record for this ID, if any
      const alteredRecord = alteredRecordsById.get(oldRecord.id);

      // If this was not one of the altered records, return the it as-is
      if (!alteredRecord) return oldRecord;

      // If the action is to delete, return null to filter this record out
      if (action === "delete") return null;

      // Otherwise, return the altered record
      return alteredRecord;
    })
    .filter((x) => !!x);
}

/**
 * Recursively apply each of the altered priority records to the provided BOM hierarchy and return a new hierarchy.
 *
 * This method can either handle a set of upserted priority records, or a set of deleted priority records, depending
 * on the value provided to the `type` argument.
 *
 * All nodes in the hierarchy are treated as immutable, that is, any unmodified nodes are copied as-is, while modified
 * nodes are returned as new objects with updated properties.
 */
function applyMutationsToBomHierarchy(
  mutationType: PriorityMutationType,
  bomHierarchy: BomNode[],
  alteredPriorityRecords: BomPathPurchasingPriority[],
): BomNode[] {
  return bomHierarchy.map((oldNode) =>
    alteredPriorityRecords.reduce(
      (newNode, alteredRecord) =>
        addOrUpdateBomNodePriorityInternal(
          defaultPurchasingPriority,
          newNode,
          alteredRecord.pathSegments,
          mutationType,
          alteredRecord,
        ),
      oldNode,
    ),
  );
}

function addOrUpdateBomNodePriorityInternal(
  /** The parent node's computed priority record. For top level nodes, this will be the default priority. */
  parentEffectivePriority: Omit<EffectivePriority, "childOverrides">,

  /** The current BOM node in the hierarchy to update */
  node: BomNode,

  /** The remaining path segments to the target node that was affected by the mutation */
  pathSegments: number[],

  mutationType: PriorityMutationType,
  alteredRecord: BomPathPurchasingPriority,
): BomNode {
  // If there are path segments remaining and the first one does not match the current node, then it is not on the update
  // path and we can return it unmodified
  const [currentPathSegment, ...remainingPathSegments] = pathSegments;
  if (currentPathSegment && currentPathSegment !== node.item.id) return node;

  // Recompute the effective priority for the current node if it is the target node that was updated by the mutation,
  // or one of its descendants. Otherwise, keep the effective priority unmodified
  const isTargetNode = currentPathSegment === node.item.id && !remainingPathSegments.length;
  const isTargetNodeDescendant = !currentPathSegment;
  const purchasingPriority =
    isTargetNode || isTargetNodeDescendant ?
      getEffectivePurchasingPriorityForUpdatedNodeOrDescendant(
        parentEffectivePriority,
        node,
        isTargetNode,
        mutationType,
        alteredRecord,
      )
    : node.purchasingPriority;

  // Recurse to update all children
  const children = node.children.map((child) =>
    addOrUpdateBomNodePriorityInternal(purchasingPriority, child, remainingPathSegments, mutationType, alteredRecord),
  );

  // Recompute the list of child overrides (note: this needs to be done for all nodes, even those above the target
  // node in the hierarchy)
  const childOverrides = getChildPurchasingPriorityOverrides(children);

  // Return the updated node
  return {
    ...node,
    children,
    purchasingPriority: {
      ...purchasingPriority,
      childOverrides,
    },
  };
}

function getEffectivePurchasingPriorityForUpdatedNodeOrDescendant(
  parentEffectivePriority: Omit<EffectivePriority, "childOverrides">,
  node: BomNode,
  isTargetNode: boolean,
  mutationType: PriorityMutationType,
  alteredRecord: BomPathPurchasingPriority,
): Omit<EffectivePriority, "childOverrides"> {
  if (!isTargetNode) {
    // This is not the target node, we don't need to update either the template or job priority records for this node
    return determineEffectivePurchasingPriority(
      parentEffectivePriority,
      node.purchasingPriority.nodeTemplateRecord,
      node.purchasingPriority.nodeJobRecord,
    );
  } else if (mutationType === "upsert") {
    // Either the template or job-specific priority record for this node was updated
    const isJobUpdate = !!alteredRecord.jobId;
    const nodeTemplateRecord = !isJobUpdate ? alteredRecord : node.purchasingPriority.nodeTemplateRecord;
    const nodeJobRecord = isJobUpdate ? alteredRecord : node.purchasingPriority.nodeJobRecord;
    return determineEffectivePurchasingPriority(parentEffectivePriority, nodeTemplateRecord, nodeJobRecord);
  } else {
    // Either the template or job-specific priority record for this node was deleted
    const isJobUpdate = !!alteredRecord.jobId;
    const nodeTemplateRecord = !isJobUpdate ? null : node.purchasingPriority.nodeTemplateRecord;
    const nodeJobRecord = isJobUpdate ? null : node.purchasingPriority.nodeJobRecord;
    return determineEffectivePurchasingPriority(parentEffectivePriority, nodeTemplateRecord, nodeJobRecord);
  }
}
